import { BestPracticeSeverity, CreateBestPractice, BestPractice } from './types';
import { useBestPractices } from './useBestPractices';
import { Button, colors } from '@sweep-io/sweep-design';
import { Stack } from '@mui/material';
import { SalesforceConfigurationType } from '@server/ai';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';
import BestPracticeEditSeverity from './BestPracticeEditSeverity';
import BestPracticeEditResource from './BestPracticeEditResource';
import { useCallback, useImperativeHandle, useState, forwardRef, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { Domain } from '@server/domain';
import EditName from '../common/editable-card/EditName';
import EditTextarea from '../common/editable-card/EditTextarea';
import EditableCardLayout from '../common/editable-card/EditableCardLayout';
import { BestPracticeSelectorsWrapper } from './BestPracticeSelectorsWrapper';
import WithPermissions from '../common/WithPermissions';
import { usePermissions } from '../../permissions/usePermissions';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';

export type NewBestPracticeCardRef = {
  resetValues: () => void;
};

interface FormState {
  name: string;
  text: string;
  severity: BestPracticeSeverity | undefined;
  resourceType: SalesforceConfigurationType | undefined;
}

const INITIAL_FORM_STATE: FormState = {
  name: '',
  text: '',
  severity: undefined,
  resourceType: undefined,
};

const NewBestPracticeCard = forwardRef<
  NewBestPracticeCardRef,
  {
    applicableDomain: Domain;
    closeCard: () => void;
    onIsPristineChange?: (isPristine: boolean) => void;
    onCreate?: (bestPractice: BestPractice) => Promise<void>;
    agent?: AiAgent | null;
  }
>(({ applicableDomain, closeCard, onIsPristineChange, onCreate, agent }, ref) => {
  const [formState, setFormState] = useState<FormState>({
    ...INITIAL_FORM_STATE,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createBestPractice } = useBestPractices(applicableDomain);

  const { name, text, severity, resourceType } = formState;
  const isValid = name.trim() !== '' && text.trim() !== '' && !!severity && !!resourceType;
  const { evaluatePermissions } = usePermissions();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const [isAllowedToCreateBestPractice] = evaluatePermissions([
    { permission: 'create:ai-agent:best-practices', crmOrgId: crmOrgId ?? '' },
  ]);

  // Check if the form is pristine by comparing with initial values
  useEffect(() => {
    const isPristine = isEqual(formState, INITIAL_FORM_STATE);
    onIsPristineChange?.(isPristine);
  }, [formState, onIsPristineChange]);

  const handleNameChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, name: value }));
  }, []);

  const handleTextChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, text: value }));
  }, []);

  const handleSeverityChange = useCallback((value: BestPracticeSeverity) => {
    setFormState((prev) => ({ ...prev, severity: value }));
  }, []);

  const handleResourceTypeChange = useCallback((value: SalesforceConfigurationType) => {
    setFormState((prev) => ({ ...prev, resourceType: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState({ ...INITIAL_FORM_STATE });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      resetValues: resetForm,
    }),
    [resetForm],
  );

  const handleCreateBestPractice = useCallback(async () => {
    if (!isValid) {
      return;
    }
    setIsSubmitting(true);
    const newBestPractice: CreateBestPractice = {
      name,
      text,
      severity,
      applicableDomain,
      applicableResourceType: resourceType,
    };

    // Create the best practice
    const createdBestPractice = await createBestPractice(newBestPractice, 0); // Add to the beginning of the list

    if (createdBestPractice && onCreate) {
      await onCreate(createdBestPractice);
    }

    setIsSubmitting(false);
    closeCard();
  }, [
    applicableDomain,
    closeCard,
    createBestPractice,
    name,
    resourceType,
    severity,
    text,
    isValid,
    onCreate,
  ]);

  const bottomLeftJsx = (
    <BestPracticeSelectorsWrapper
      isEditMode={true}
      severityJsx={
        <BestPracticeEditSeverity setSeverity={handleSeverityChange} severity={severity} />
      }
      resourceJsx={
        <BestPracticeEditResource
          setResourceType={handleResourceTypeChange}
          resourceType={resourceType}
          agent={agent}
        />
      }
    />
  );

  const saveButtonJsx = (
    <Button
      variant="primary"
      size="tiny"
      onClick={handleCreateBestPractice}
      loading={isSubmitting}
      disabled={!isValid}
    >
      Save
    </Button>
  );

  return (
    <EditableCardLayout
      borderColor={colors.blue[600]}
      nameJsx={
        <EditName
          setName={handleNameChange}
          name={name}
          placeholder="Enter a name for this best practice"
        />
      }
      textJsx={
        <EditTextarea
          setText={handleTextChange}
          text={text}
          placeholder="Define a standard to follow..."
        />
      }
      bottomLeftJsx={bottomLeftJsx}
      updateCardActionsJsx={
        <Stack direction="row" gap={1}>
          <Button variant="system" size="tiny" onClick={closeCard} disabled={isSubmitting}>
            Cancel
          </Button>
          {isAllowedToCreateBestPractice ? (
            saveButtonJsx
          ) : (
            <WithPermissions
              accessValidation={{
                type: 'query',
                queries: [
                  { permission: 'create:ai-agent:best-practices', crmOrgId: crmOrgId ?? '' },
                ],
              }}
              disabledTooltip="To create a best practice, please contact your admin."
            >
              {saveButtonJsx}
            </WithPermissions>
          )}
        </Stack>
      }
    />
  );
});

export default NewBestPracticeCard;
